import i18next from "i18next";

import { SINGLE_UNIT_THRESHOLD } from "@/utils/getDeterministicDiscountedPrice";
import type { ListingTypeOptions } from "@/utils/productTypes";
import { Models } from "api/types";

import { formatCurrency } from "../toolbox/Money";

export const hasLinearPricing = (packagePrices: Models<"PackagePriceModel">[] | undefined) => {
  if (!packagePrices) return false;
  return packagePrices
    .map((item: any, index: any, array: any) => {
      if (index === 0) return true; // First element always follows a linear pattern
      return item["quantity"] === array[index - 1]["quantity"] + 1;
    })
    .every(Boolean);
};

export const getPricingText = (
  hasLinearPricing: boolean | undefined,
  price: number,
  unitType: string | null | undefined,
  type: ListingTypeOptions,
) => {
  const formattedPrice = formatCurrency(price);

  if (type === "Membership") {
    return i18next.t("listingHelperService.pricePerMonth", { price: formattedPrice });
  }

  if (!hasLinearPricing && type === "Treatment") {
    return i18next.t("listingHelperService.fromPrice", { price: formattedPrice });
  }

  // A Package listing prices the whole bundle, so its items' unit type (syringe, vial, unit)
  // is not the price unit.
  if (hasLinearPricing && type === "Package") {
    return i18next.t("listingHelperService.pricePerPackage", { price: formattedPrice });
  }

  const listingUnitType = hasLinearPricing ? `/${unitType}` : "";
  return formattedPrice + listingUnitType;
};

export const getFreeGiftPackageIds = (
  promotions: Models<"PromotionModel">[] | undefined,
): Set<string> => {
  if (!promotions) return new Set<string>();

  return new Set(
    promotions
      .flatMap((promo) => promo.freePackageGroup?.freePackageGroupInclusions ?? [])
      .map((inclusion) => inclusion.packageId)
      .filter((packageId): packageId is string => !!packageId),
  );
};

export const getFreeGiftListingIds = (
  promotions: Models<"PromotionModel">[] | undefined,
  listings: Array<{ id: string; packages?: object[] | null }> | undefined,
): Set<string> => {
  if (!listings) return new Set<string>();

  const packageIds = getFreeGiftPackageIds(promotions);

  return new Set(
    listings
      .filter((listing) =>
        listing.packages?.some((pkg) => "id" in pkg && packageIds.has((pkg as { id: string }).id)),
      )
      .map((listing) => listing.id),
  );
};

export const getFreeGiftPromotion = (
  promotions: Models<"PromotionModel">[] | undefined,
  listingId: string,
  listings: Array<{ id: string; packages?: object[] | null }> | undefined,
): Models<"PromotionModel"> | undefined => {
  if (!promotions || !listings) return undefined;

  const listing = listings.find((l) => l.id === listingId);
  if (!listing?.packages) return undefined;

  const listingPackageIds = new Set(
    listing.packages
      .filter(
        (pkg): pkg is { id: string } =>
          "id" in pkg && typeof (pkg as { id: string }).id === "string",
      )
      .map((pkg) => pkg.id),
  );

  return promotions.find((promo) =>
    promo.freePackageGroup?.freePackageGroupInclusions?.some(
      (inclusion) => inclusion.packageId && listingPackageIds.has(inclusion.packageId),
    ),
  );
};

export const getDiscountText = (
  shopListingDiscounts: Models<"ShopProductDiscountModel"> | undefined,
  type?: "Treatment" | "Package" | "Membership" | "Product" | "ProductFromExternalSource",
) => {
  const { maxAmountOff, maxPercentageOff, isVariable, minimumQuantity } =
    shopListingDiscounts || {};

  const isBuyXGetY = shopListingDiscounts?.isBuyXGetY ?? false;

  const amountOff = maxAmountOff && maxAmountOff > 0 ? maxAmountOff : undefined;
  const percentageOff = maxPercentageOff && maxPercentageOff > 0 ? maxPercentageOff : undefined;

  if (!amountOff && !percentageOff) return "";

  if (!isBuyXGetY && shopListingDiscounts?.buyXGetYDiscountRole === "Requirement") {
    return i18next.t("listingHelperService.buyMoreAndSave");
  }

  if (isBuyXGetY) {
    return getTagBXGYDiscountText(
      shopListingDiscounts?.matchesRequirementsAndInclusions ?? false,
      percentageOff,
    );
  }

  if (type === "Membership" && percentageOff === 1) {
    return i18next.t("listingHelperService.freeMonth");
  }

  const amountText = amountOff
    ? i18next.t("listingHelperService.amountOff", { amount: formatCurrency(amountOff) })
    : "";
  const percentageText = percentageOff
    ? i18next.t("listingHelperService.percentOff", { percent: Math.round(percentageOff * 100) })
    : "";
  const discountText = amountText || percentageText;

  if (isVariable && discountText) {
    return i18next.t("listingHelperService.upToDiscount", { discount: discountText });
  }

  // The Beta campaign builder writes "$10 off Botox when you buy 20" as a quantity threshold on the
  // discounted package itself. A badge that names only the saving reads as a price the shopper gets on
  // the single unit in front of them, so the threshold is stated instead. The evaluator charges the cash
  // amount against every qualifying unit, which is why the amount copy says "each".
  if (typeof minimumQuantity === "number" && minimumQuantity > SINGLE_UNIT_THRESHOLD) {
    if (amountOff) {
      return i18next.t("listingHelperService.buyQuantitySaveAmount", {
        quantity: minimumQuantity,
        amount: formatCurrency(amountOff),
      });
    }

    if (percentageOff) {
      return i18next.t("listingHelperService.buyQuantitySavePercent", {
        quantity: minimumQuantity,
        percent: Math.round(percentageOff * 100),
      });
    }
  }

  return discountText;
};

export const getTagBXGYDiscountText = (isSamePackage: boolean, percentOff: number | undefined) => {
  if (isSamePackage && percentOff !== 1) {
    return i18next.t("listingHelperService.buyOneGetOne");
  }

  if (percentOff === 1 || (isSamePackage && percentOff === 1)) {
    return i18next.t("listingHelperService.freeGift");
  }

  return i18next.t("listingHelperService.buyMoreAndSave");
};

export const getDiscountCustomColor = (
  shopListingDiscounts: Models<"ShopProductDiscountModel"> | undefined,
  customShopCategories: Models<"CategoryListingsResult">[] | undefined,
) => {
  if (!shopListingDiscounts) return null;
  return shopListingDiscounts?.isFromSiteWidePromo &&
    customShopCategories &&
    customShopCategories.length > 0
    ? customShopCategories[0].promotion?.backgroundColor
    : null;
};

export const getListingsWithDiscountInformation = <T extends { id: string }>(
  listings: T[],
  customShopCategories: Models<"CategoryListingsResult">[] | undefined = undefined,
  listingDiscounts: {
    [key: string]: Models<"ShopProductDiscountModel"> | undefined;
  } = {},
  freeGiftListingIds?: Set<string>,
) => {
  if (!listings?.length) return [];
  return listings.map((listing) => {
    const rawDiscountData = listingDiscounts[listing.id];
    // Filter out signup reward discounts - they should not be displayed
    const discountData = rawDiscountData?.isSignUpReward ? undefined : rawDiscountData;

    const discountText = getDiscountText(discountData);
    const finalDiscountText = freeGiftListingIds?.has(listing.id)
      ? i18next.t("listingHelperService.freeGift")
      : discountText;

    return {
      ...listing,
      discountText: finalDiscountText,
      discount: discountData,
      customColor: getDiscountCustomColor(discountData, customShopCategories),
    };
  });
};

export const getMembershipsWithDiscountInformation = <T extends { id: string }>(
  memberships: T[],
  shopDiscounts: Models<"ShopDiscountModel"> | undefined,
  customShopCategories: Models<"CategoryListingsResult">[] | undefined,
) => {
  if (!memberships || !memberships.length) return [];
  return memberships.map((membership) => {
    const rawDiscountData = shopDiscounts?.membershipDiscounts?.[membership.id];
    // Filter out signup reward discounts - they should not be displayed
    const discountData = rawDiscountData?.isSignUpReward ? undefined : rawDiscountData;
    return {
      ...membership,
      discountText: getDiscountText(discountData, "Membership"),
      customColor: getDiscountCustomColor(discountData, customShopCategories),
      discount: discountData,
    };
  });
};

export const getRetailProductsWithDiscountInformation = <T extends { id: string }>(
  retailProducts: T[],
  customShopCategories: Models<"CategoryListingsResult">[] | undefined = undefined,
  listingDiscounts: {
    [key: string]: Models<"ShopProductDiscountModel"> | undefined;
  } = {},
  freeGiftListingIds?: Set<string>,
) => {
  if (!retailProducts?.length) return [];
  return retailProducts.map((product) => {
    const rawDiscountData = listingDiscounts[product.id];
    // Filter out signup reward discounts - they should not be displayed
    const discountData = rawDiscountData?.isSignUpReward ? undefined : rawDiscountData;

    const discountText = getDiscountText(discountData);
    const finalDiscountText = freeGiftListingIds?.has(product.id)
      ? i18next.t("listingHelperService.freeGift")
      : discountText;

    return {
      ...product,
      discountText: finalDiscountText,
      discount: discountData,
      customColor: getDiscountCustomColor(discountData, customShopCategories),
    };
  });
};

export const getLowestRetailProductVariant = (
  product: Pick<Models<"DisplayRetailProductListing">, "oneTimePurchasePriceInCents" | "variants">,
): { price: number; variantId: string | undefined } => {
  if (!product.variants?.length) {
    return {
      price: product.oneTimePurchasePriceInCents,
      variantId: undefined,
    };
  }
  const cheapest = product.variants.reduce((min, v) =>
    v.retailProductPrice.priceInCents < min.retailProductPrice.priceInCents ? v : min,
  );
  return {
    price: cheapest.retailProductPrice.priceInCents,
    variantId: cheapest.id,
  };
};

export const getLowestRetailProductVariantPrice = (
  product: Pick<Models<"DisplayRetailProductListing">, "oneTimePurchasePriceInCents" | "variants">,
): number => getLowestRetailProductVariant(product).price;

export const getInclusionsCount = (
  inclusions: Models<"InclusionListingsModel"> | undefined,
): number => {
  if (!inclusions) return 0;

  const packageCount = inclusions.packages.length;
  const treatmentCount = inclusions.treatments.reduce(
    (acc, t) => acc + (t.variants.length || 1),
    0,
  );
  const membershipCount = inclusions.memberships.length;
  const productCount = inclusions.products.length;

  return packageCount + treatmentCount + membershipCount + productCount;
};
